import { useState, useCallback, useRef } from 'react'
import { motion } from 'framer-motion'
import { Globe, Shield, Fingerprint, Lock } from 'lucide-react'
import { useTheme } from '../contexts/ThemeContext'
import { useLocale } from '../hooks/useLocale'

// Dev mode port mapping for local development
const devPortMap: Record<string, number> = {
  'lukhas.ai': 3004,
  'lukhas.id': 3001,
  'lukhas.com': 3002,
  'lukhas.us': 3003,
  'lukhas.cloud': 3005,
  'lukhas.dev': 3006,
  'lukhas.io': 3007,
  'lukhas.eu': 3008,
  'lukhas.app': 3010,
  'lukhas.store': 3011,
  'lukhas.xyz': 3012,
  'lukhas.team': 3013,
}

const getEcosystemHref = (domain: string): string => {
  const isDev = typeof window !== 'undefined' && window.location.hostname === 'localhost'
  if (isDev) {
    const port = devPortMap[domain]
    return port ? `http://localhost:${port}` : `https://${domain}`
  }
  return `https://${domain}`
}

const footerSections = {
  identity: {
    title: 'Identity',
    links: [
      { label: 'Create AiD', href: '/register' },
      { label: 'Namespaces', href: '/#namespaces' },
      { label: 'Security', href: '/#security' },
      { label: 'Wallet Pass', href: '/#wallet' },
      { label: 'Dashboard', href: '/dashboard' },
    ],
  },
  platform: {
    title: 'Platform',
    links: [
      { label: 'LUKHAS AI', href: getEcosystemHref('lukhas.ai') },
      { label: 'Developers', href: getEcosystemHref('lukhas.dev') },
      { label: 'Cloud', href: getEcosystemHref('lukhas.cloud') },
      { label: 'API Reference', href: '/docs/api' },
      { label: 'Status', href: '/status' },
    ],
  },
  company: {
    title: 'Company',
    links: [
      { label: 'About', href: '/about' },
      { label: 'Research', href: '/research' },
      { label: 'Brand', href: '/brand' },
      { label: 'Careers', href: '/careers' },
      { label: 'Contact', href: '/contact' },
    ],
  },
  legal: {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Cookie Policy', href: '/cookies' },
      { label: 'Compliance', href: '/compliance' },
      { label: 'Security Disclosure', href: '/security' },
    ],
  },
}

// Ecosystem links — AiD at visual center (index 5 of 11 items)
// Order: 5 left | AiD (center, active) | 5 right
const ecosystemLinks = [
  // Left side (5)
  { domain: 'lukhas.com', label: '', description: 'LUKHAS Corporate' },
  { domain: 'lukhas.dev', label: 'Dev', description: 'LUKHAS Developers' },
  { domain: 'lukhas.io', label: 'iO', description: 'LUKHAS I/O' },
  { domain: 'lukhas.ai', label: 'I', description: 'LUKHAS AI Platform' },
  { domain: 'lukhas.cloud', label: 'Cloud', description: 'LUKHAS Cloud' },
  // Center (current site)
  { domain: 'lukhas.id', label: 'iD', description: 'LUKHAS Identity', active: true },
  // Right side (5)
  { domain: 'lukhas.app', label: 'App', description: 'LUKHAS App' },
  { domain: 'lukhas.us', label: 'US', description: 'LUKHAS US' },
  { domain: 'lukhas.eu', label: 'EU', description: 'LUKHAS EU' },
  { domain: 'lukhas.store', label: 'Store', description: 'LUKHAS Store' },
  { domain: 'lukhas.xyz', label: 'XYZ', description: 'LUKHAS XYZ' },
]

const CENTER_INDEX = 5

const trustBadges = [
  { icon: Shield, label: 'SOC 2 Type II', sublabel: 'Compliance' },
  { icon: Fingerprint, label: 'WebAuthn', sublabel: 'Passwordless' },
  { icon: Lock, label: 'Zero-Knowledge', sublabel: 'Architecture' },
]

/**
 * EcosystemLinks — Interactive cascading glow animation
 * 4-phase animation: ripple out -> return to center -> center breathes -> all illuminate
 */
function EcosystemLinks({ isDark }: { isDark: boolean }) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [glowingIndices, setGlowingIndices] = useState<Set<number>>(new Set())
  const [glowIntensity, setGlowIntensity] = useState<Record<number, number>>({})
  const [isAnimating, setIsAnimating] = useState(false)
  const timeoutsRef = useRef<NodeJS.Timeout[]>([])

  const clearTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout)
    timeoutsRef.current = []
  }, [])

  const triggerCascade = useCallback((fromIndex: number) => {
    if (isAnimating) return
    setIsAnimating(true)
    clearTimeouts()

    const total = ecosystemLinks.length
    const events: { index: number; delay: number; intensity: number; duration: number }[] = []

    // Phase 1: Slow ripple outward from origin (150ms between each)
    const rippleDelay = 150
    for (let distance = 0; distance <= Math.max(fromIndex, total - 1 - fromIndex); distance++) {
      const leftIndex = fromIndex - distance
      const rightIndex = fromIndex + distance
      const intensity = Math.max(0.4, 1 - distance * 0.1)

      if (leftIndex >= 0) {
        events.push({ index: leftIndex, delay: distance * rippleDelay, intensity, duration: 600 })
      }
      if (rightIndex < total && rightIndex !== leftIndex) {
        events.push({ index: rightIndex, delay: distance * rippleDelay, intensity, duration: 600 })
      }
    }

    // Phase 2: Graceful return wave to center
    const phase1End = Math.max(...events.map(e => e.delay)) + 800
    const returnDelay = 120
    const maxDistance = Math.max(CENTER_INDEX, total - 1 - CENTER_INDEX)

    for (let distance = maxDistance; distance >= 0; distance--) {
      const leftIndex = CENTER_INDEX - distance
      const rightIndex = CENTER_INDEX + distance
      const intensity = 0.6 + (maxDistance - distance) * 0.08

      if (leftIndex >= 0) {
        events.push({
          index: leftIndex,
          delay: phase1End + (maxDistance - distance) * returnDelay,
          intensity,
          duration: 500,
        })
      }
      if (rightIndex < total && rightIndex !== leftIndex) {
        events.push({
          index: rightIndex,
          delay: phase1End + (maxDistance - distance) * returnDelay,
          intensity,
          duration: 500,
        })
      }
    }

    // Phase 3: Center breathes deeply
    const phase2End = phase1End + maxDistance * returnDelay + 600
    events.push({ index: CENTER_INDEX, delay: phase2End, intensity: 1, duration: 800 })

    // Phase 4: All links softly illuminate together, then fade
    const phase3End = phase2End + 500
    for (let i = 0; i < total; i++) {
      events.push({ index: i, delay: phase3End, intensity: 0.7, duration: 1000 })
    }

    // Execute with smooth intensity tracking
    events.forEach(({ index, delay, intensity, duration }) => {
      const timeout = setTimeout(() => {
        setGlowingIndices(prev => new Set([...prev, index]))
        setGlowIntensity(prev => ({ ...prev, [index]: intensity }))

        const fadeTimeout = setTimeout(() => {
          setGlowIntensity(prev => ({ ...prev, [index]: 0 }))
          setTimeout(() => {
            setGlowingIndices(prev => {
              const next = new Set(prev)
              next.delete(index)
              return next
            })
          }, 300)
        }, duration)
        timeoutsRef.current.push(fadeTimeout)
      }, delay)
      timeoutsRef.current.push(timeout)
    })

    // Final cleanup
    const finalTimeout = setTimeout(() => {
      setIsAnimating(false)
      setGlowingIndices(new Set())
      setGlowIntensity({})
    }, phase3End + 1500)
    timeoutsRef.current.push(finalTimeout)
  }, [isAnimating, clearTimeouts])

  const handleMouseEnter = (index: number) => {
    clearTimeouts()
    setGlowingIndices(new Set())
    setGlowIntensity({})
    setIsAnimating(false)
    setHoveredIndex(index)
  }

  const handleMouseLeave = (index: number) => {
    setHoveredIndex(null)
    triggerCascade(index)
  }

  // Teal accent instead of cyan
  const accentColor = isDark ? 'rgb(20, 184, 166)' : 'rgb(13, 148, 136)'

  return (
    <div className="flex flex-wrap justify-between items-center gap-y-4 mb-8">
      {ecosystemLinks.map((item, index) => {
        const isHovered = hoveredIndex === index
        const isGlowing = glowingIndices.has(index)
        const intensity = glowIntensity[index] || 0
        const isActive = item.active
        const href = getEcosystemHref(item.domain)

        return (
          <motion.a
            key={item.domain}
            href={href}
            aria-label={item.description}
            title={item.description}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={() => handleMouseLeave(index)}
            className="text-base font-brand tracking-wide cursor-pointer select-none"
            animate={{
              scale: isHovered ? 1.8 : isGlowing ? 1 + intensity * 0.25 : isActive ? 1.15 : 1,
              opacity: isHovered ? 1 : isGlowing ? 0.7 + intensity * 0.3 : isActive ? 1 : 0.4,
            }}
            transition={{
              duration: isHovered ? 0.4 : 0.5,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            style={{
              color: isHovered
                ? isDark ? '#ffffff' : '#0f172a'
                : isGlowing
                ? `rgba(20, 184, 166, ${0.6 + intensity * 0.4})`
                : isActive
                ? accentColor
                : isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(15, 23, 42, 0.4)',
              filter: isHovered
                ? `drop-shadow(0 0 25px ${isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(13, 148, 136, 0.6)'}) drop-shadow(0 0 50px rgba(20, 184, 166, 0.5))`
                : isGlowing
                ? `drop-shadow(0 0 ${15 + intensity * 20}px rgba(20, 184, 166, ${0.5 + intensity * 0.4}))`
                : isActive
                ? 'drop-shadow(0 0 12px rgba(20, 184, 166, 0.5))'
                : 'none',
              textShadow: isHovered
                ? `0 0 40px ${isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(13, 148, 136, 0.6)'}, 0 0 80px rgba(20, 184, 166, 0.4)`
                : isGlowing
                ? `0 0 ${20 + intensity * 30}px rgba(20, 184, 166, ${0.4 + intensity * 0.4})`
                : isActive
                ? '0 0 20px rgba(20, 184, 166, 0.5)'
                : 'none',
              transition: 'color 0.5s ease, filter 0.5s ease, text-shadow 0.5s ease',
            }}
          >
            {'\u039B'}{item.label && <span className="text-[10px] align-top">{item.label}</span>}
          </motion.a>
        )
      })}
    </div>
  )
}

/**
 * LanguageIndicator — read-only current-language display
 */
function LanguageIndicator({ isDark }: { isDark: boolean }) {
  const { locale } = useLocale()
  const lang = locale.toUpperCase()
  return (
    <span
      className={`flex items-center gap-1 text-[10px] tracking-[0.24em] uppercase select-none ${
        isDark ? 'text-white/22' : 'text-slate-400/70'
      }`}
    >
      <Globe className="w-2.5 h-2.5 opacity-60" />
      {lang}
    </span>
  )
}

export default function IdFooter() {
  const { isDark } = useTheme()

  const linkClass = `text-[11px] leading-tight transition-all duration-200 hover:translate-x-0.5 ${
    isDark
      ? 'text-white/50 hover:text-white/90'
      : 'text-slate-500 hover:text-slate-800'
  }`

  const headerClass = `text-[10px] font-semibold mb-3 uppercase tracking-[0.15em] ${
    isDark ? 'text-white/70' : 'text-slate-600'
  }`

  return (
    <footer className={`relative z-10 border-t backdrop-blur-xl ${
      isDark
        ? 'border-white/5 bg-slate-950/80'
        : 'border-slate-200/80 bg-white/90'
    }`}>
      {/* Identity Pulse — teal gradient line scales from center on scroll */}
      <motion.div
        className="absolute top-0 left-0 right-0 h-px origin-center"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(20, 184, 166, 0.4), transparent)',
        }}
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
      />

      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* 4-Column Link Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          {Object.entries(footerSections).map(([key, section]) => (
            <div key={key}>
              <h4 className={headerClass}>{section.title}</h4>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <a href={link.href} className={linkClass}>
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Trust/Security Badges */}
        <div className="flex flex-wrap justify-center gap-4 mb-10">
          {trustBadges.map((badge) => (
            <div
              key={badge.label}
              className={`flex items-center gap-2.5 px-4 py-2 rounded-full border ${
                isDark
                  ? 'border-white/5 bg-white/[0.02]'
                  : 'border-slate-200/50 bg-slate-50/50'
              }`}
            >
              <badge.icon className={`w-3.5 h-3.5 ${isDark ? 'text-teal-400/60' : 'text-teal-600/60'}`} />
              <span className={`text-[10px] font-medium tracking-wide ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
                {badge.label}
              </span>
              <span className={`text-[9px] tracking-wide ${isDark ? 'text-white/25' : 'text-slate-400'}`}>
                {badge.sublabel}
              </span>
            </div>
          ))}
        </div>

        {/* Ecosystem Links with Cascading Glow */}
        <div className={`pt-8 border-t ${isDark ? 'border-slate-800/50' : 'border-slate-200/50'}`}>
          <EcosystemLinks isDark={isDark} />

          {/* Brand statement */}
          <div className="text-center mb-6">
            <p className={`text-[10px] tracking-[0.3em] uppercase font-light ${
              isDark ? 'text-teal-400/30' : 'text-teal-600/30'
            }`}>
              Your Identity. Your Sovereignty. Your AiD.
            </p>
          </div>

          {/* Bottom bar — copyright left, language indicator right */}
          <div className="flex items-center justify-between gap-4">
            <span className={`text-[10px] font-light tracking-wide ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              Logic Unified Knowledge Hyper Adaptable Systems &copy; {new Date().getFullYear()}
            </span>
            <LanguageIndicator isDark={isDark} />
          </div>
        </div>
      </div>
    </footer>
  )
}
